// These are public Firebase web identifiers, not server credentials.
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyDnwuyRYS00zDrGCES9wXKpMC8oFiJM4zo",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "trailpulse-otp.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "trailpulse-otp",
  storageBucket: "trailpulse-otp.firebasestorage.app",
  messagingSenderId: "963058576378",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:963058576378:web:d424c27848ff6f82ce9243",
};

let sdkPromise;
async function loadSdk() {
  if (!sdkPromise) {
    sdkPromise = (async () => {
      const appSdk = await import("firebase/app");
      const authSdk = await import("firebase/auth");
      const existing = appSdk.getApps().find((app) => app.name === "trailpulse-phone-otp");
      const app = existing || appSdk.initializeApp(firebaseConfig, "trailpulse-phone-otp");
      const auth = existing ? authSdk.getAuth(app) : authSdk.initializeAuth(app, {
        persistence: authSdk.inMemoryPersistence,
      });
      auth.useDeviceLanguage();
      return { ...authSdk, auth };
    })().catch((error) => { sdkPromise = null; throw error; });
  }
  return sdkPromise;
}

export function normalizePhone(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  const phone = !raw.startsWith("+") && digits.length === 10 ? `+91${digits}` : `+${digits}`;
  if (!/^\+[1-9]\d{7,14}$/.test(phone)) throw new Error("Enter a valid phone number with country code.");
  return phone;
}

export function formatPhoneAuthError(error) {
  const messages = {
    "auth/invalid-phone-number": "Enter a valid phone number with country code.",
    "auth/invalid-verification-code": "Incorrect code. Please try again.",
    "auth/code-expired": "This code has expired. Request a new code.",
    "auth/session-expired": "This code has expired. Request a new code.",
    "auth/too-many-requests": "Too many attempts. Please wait before requesting another code.",
    "auth/quota-exceeded": "SMS verification is temporarily unavailable. Please use email or try later.",
    "auth/captcha-check-failed": "The security check failed. Please request a new code.",
    "auth/missing-app-credential": "The security check failed. Please request a new code.",
    "auth/invalid-app-credential": "Firebase rejected the phone verification security check. Please request a new code or use email.",
    "auth/unauthorized-domain": "Phone verification is not available on this domain. Please use email.",
    "auth/operation-not-allowed": "Firebase is blocking SMS for this project or region. Please use email.",
    "auth/network-request-failed": "Unable to reach phone verification. Check your connection and try again.",
    "auth/invalid-api-key": "Phone verification is not configured correctly. Please use email.",
    "auth/billing-not-enabled": "Firebase SMS billing is not enabled for this project. Please use email.",
  };
  const formatted = new Error(messages[error?.code] || error?.message || "Phone verification failed. Please try again.");
  if (typeof error?.code === "string") formatted.code = error.code;
  return formatted;
}

export function createPhoneVerification() {
  let pending = null;
  let verifier = null;
  let container = null;
  let generation = 0;

  const clearCaptcha = () => {
    if (verifier) verifier.clear();
    verifier = null;
    container?.remove();
    container = null;
  };
  const clear = () => {
    generation += 1;
    pending = null;
    clearCaptcha();
  };

  const send = async (delivery, purpose) => {
    clear();
    const request = generation;
    try {
      const phone = normalizePhone(delivery.phone);
      const sdk = await loadSdk();
      if (request !== generation) throw new Error("Phone verification was cancelled. Request a new code.");
      container = document.createElement("div");
      document.body.appendChild(container);
      verifier = new sdk.RecaptchaVerifier(sdk.auth, container, { size: "invisible" });
      const confirmation = await sdk.signInWithPhoneNumber(sdk.auth, phone, verifier);
      if (request !== generation) throw new Error("Phone verification was cancelled. Request a new code.");
      pending = { phone, purpose, delivery, confirmation, proof: null };
      clearCaptcha();
      return { ...delivery, sent: true };
    } catch (error) {
      if (request === generation) clear();
      throw formatPhoneAuthError(error);
    }
  };

  const confirm = async (phone, code, purpose) => {
    const attempt = pending;
    if (!attempt || attempt.phone !== normalizePhone(phone) || attempt.purpose !== purpose) {
      throw new Error("Request a new code before verifying.");
    }
    if (Date.parse(attempt.delivery.expires_at) <= Date.now()) {
      clear();
      throw new Error("This code has expired. Request a new code.");
    }
    if (!/^\d{6}$/.test(String(code).trim())) throw new Error("Enter the 6-digit code.");
    try {
      if (!attempt.proof) {
        const credential = await attempt.confirmation.confirm(String(code).trim());
        const token = await credential.user.getIdToken(true);
        const sdk = await loadSdk();
        await sdk.signOut(sdk.auth);
        attempt.proof = token;
      }
      if (pending !== attempt) throw new Error("Request a new code before verifying.");
      return { firebase_id_token: attempt.proof, challenge_id: attempt.delivery.challenge_id };
    } catch (error) {
      throw formatPhoneAuthError(error);
    }
  };

  return { send, confirm, clear };
}
